/**
 * Button — Spec 020 D2 on the brand «Открыто» (DS `Button`).
 *
 * `primary` is the yellow one: one per screen, and on screens with the bottom
 * bar that one is «Записать», so a screen's own main action is `strong` — the
 * default. Destructive actions are an outline in the text colour with a
 * confirmation step; the «Препятствие» red belongs to that status only.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cn } from "@/lib/cn";

export type ButtonVariant =
  | "primary"
  | "strong"
  | "secondary"
  | "ghost"
  | "destructive"
  | "destructive-outline";

export type ButtonSize = "default" | "compact" | "icon";

/** Pressed state is a brightness shift, never a second colour. */
const DANGER = "border border-p-border-strong bg-transparent text-p-text press-surface";
const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-p-accent text-p-on-accent press-surface",
  strong: "bg-p-text text-p-bg press-fill",
  secondary: "border border-p-border bg-p-surface-2 text-p-text press-surface",
  ghost: "bg-transparent text-p-link press-surface",
  // The confirming step of a destruction looks the same as the first: what
  // tells them apart is the question next to it, not a red fill.
  destructive: DANGER,
  "destructive-outline": DANGER,
};

/** DS: 48 / radius 12 for the main control, 36 / radius 8 compact, 36 square for icons. */
const SIZE: Record<ButtonSize, string> = {
  default: "h-12 rounded-xl px-6 text-[16px] font-semibold gap-2",
  compact: "h-compact rounded-compact px-3 text-[14px] font-semibold gap-1.5 hit-44",
  icon: "h-compact w-compact rounded-compact shrink-0 hit-44",
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the label for a spinner and blocks input (§5). */
  loading?: boolean;
  /** Label shown while `loading` — the spec asks for a process verb ("Сохраняем…"). */
  loadingText?: string;
  /** Stretches to the row; the spec's sheet pairs one fixed + one growing button. */
  block?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

/** DS `p-spin`: a 16px ring in the label's colour with a gap, on every variant. */
function ButtonSpinner() {
  return (
    <span
      aria-hidden="true"
      className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "strong",
    size = "default",
    loading = false,
    loadingText,
    block = false,
    icon,
    children,
    className,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const label = loading ? (loadingText ?? children) : children;

  return (
    <button
      ref={ref}
      type={type}
      // §5 — a button in flight must not be able to fire twice.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "relative inline-flex select-none items-center justify-center whitespace-nowrap",
        // Disabled dims without shifting hue.
        "disabled:opacity-55",
        VARIANT[variant],
        SIZE[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <ButtonSpinner /> : icon}
      {label}
    </button>
  );
});
